/**
 * Admin Center · Reiter „Montage" (MONTAGEKALENDER.md 5.2 und 8.4).
 *
 * Drei Dinge, die außerhalb des Codes gehören: in welchem Postfach der
 * Kalender liegt, wie er heißt, und wer montiert. Stünden sie im Code,
 * wäre ein Wechsel der Zuständigkeit ein Deploy.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { useKora } from '../zustand';
import './montageverwaltung.css';

interface MonteurZeile {
  id: string;
  name: string;
  zusatz: string | null;
  kuerzel: string;
  aktiv: boolean;
  sortierung: number;
}

interface MontageStand {
  postfach: string;
  kalenderName: string;
  kalenderGefunden: boolean;
  monteure: MonteurZeile[];
}

export function MontageVerwaltung() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const abfrage = useQuery({
    queryKey: ['admin-montage'],
    queryFn: () => api.hole<MontageStand>('/admin/montage'),
  });
  const d = abfrage.data;

  const [postfach, setPostfach] = useState('');
  const [kalender, setKalender] = useState('');
  const [neu, setNeu] = useState({ name: '', zusatz: '' });

  useEffect(() => {
    if (!d) return;
    setPostfach(d.postfach);
    setKalender(d.kalenderName);
  }, [d]);

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-montage'] });
    void queryClient.invalidateQueries({ queryKey: ['montage'] });
  };

  const speichern = useMutation({
    mutationFn: (daten: { postfach?: string; kalenderName?: string }) =>
      api.aendere<{ ok: true }>('/admin/montage', daten),
    onSuccess: () => {
      neuLaden();
      toastZeigen('Gespeichert – Kora sucht den Kalender beim nächsten Öffnen neu.');
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const suchen = useMutation({
    mutationFn: () => api.sende<{ toast: string }>('/admin/montage/kalender-suchen'),
    onSuccess: (a) => {
      neuLaden();
      toastZeigen(a.toast);
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Der Kalender wurde nicht gefunden.'),
  });

  const anlegen = useMutation({
    mutationFn: () => api.sende<MonteurZeile>('/admin/montage/monteure', neu),
    onSuccess: () => {
      setNeu({ name: '', zusatz: '' });
      neuLaden();
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Anlegen fehlgeschlagen.'),
  });

  const aendern = useMutation({
    mutationFn: ({ id, daten }: { id: string; daten: Partial<MonteurZeile> }) =>
      api.aendere<{ ok: true }>(`/admin/montage/monteure/${id}`, daten),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung fehlgeschlagen.'),
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => api.loesche<{ ok: true }>(`/admin/montage/monteure/${id}`),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Löschen nicht möglich.'),
  });

  if (!d) return <div className="modal-hinweis">Wird geladen …</div>;

  return (
    <div className="mv">
      <section className="mv-block">
        <div className="mv-titel">Kalender in Outlook</div>
        <p className="mv-text">
          Kora schreibt die Montagen in einen bestehenden Outlook-Kalender. Termine, die jemand dort
          direkt einträgt, zeigt Kora an, ändert sie aber nie.
        </p>

        <label className="mv-feld">
          <span className="mv-label">Postfach</span>
          <input
            value={postfach}
            placeholder="tibor.poos-kallinger@eichenherz.at"
            onChange={(e) => setPostfach(e.target.value)}
            onBlur={() => postfach !== d.postfach && speichern.mutate({ postfach })}
          />
          <span className="mv-hinweis">Das Postfach, in dem der Kalender liegt – für alle freigegeben.</span>
        </label>

        <label className="mv-feld">
          <span className="mv-label">Kalendername</span>
          <input
            value={kalender}
            placeholder="MONTAGE - Termine"
            onChange={(e) => setKalender(e.target.value)}
            onBlur={() => kalender !== d.kalenderName && speichern.mutate({ kalenderName: kalender })}
          />
        </label>

        <div className="mv-zeile">
          <span className={`mv-stand ${d.kalenderGefunden ? 'ok' : ''}`}>
            {d.kalenderGefunden
              ? 'Kalender gefunden – Kora kennt seine Kennung.'
              : 'Kalender noch nicht gesucht.'}
          </span>
          <button
            type="button"
            className="mv-sek"
            disabled={suchen.isPending || !postfach}
            onClick={() => suchen.mutate()}
          >
            {suchen.isPending ? 'Sucht …' : 'Kalender suchen'}
          </button>
        </div>
      </section>

      <section className="mv-block">
        <div className="mv-titel">Monteure</div>
        <p className="mv-text">
          Wer montiert. Die Liste steht im Dialog „Montage eintragen" zur Auswahl. Monteure haben kein
          Postfach und melden sich nicht in Kora an – sie stehen im Kalender und in der Auslastung.
        </p>

        <div className="mv-liste">
          {d.monteure.map((m) => (
            <div key={m.id} className={`mv-monteur ${m.aktiv ? '' : 'inaktiv'}`}>
              <span className="mv-kuerzel">{m.kuerzel}</span>
              <span className="mv-name">
                {m.name}
                {m.zusatz ? <span className="mv-zusatz"> · {m.zusatz}</span> : null}
              </span>
              <button
                type="button"
                className="mv-schalter"
                title={m.aktiv ? 'Steht zur Auswahl' : 'Steht nicht mehr zur Auswahl'}
                onClick={() => aendern.mutate({ id: m.id, daten: { aktiv: !m.aktiv } })}
              >
                {m.aktiv ? 'aktiv' : 'nicht aktiv'}
              </button>
              {/*
                Löschen geht nur, solange kein Einsatz an ihm hängt – das
                prüft der Server. Wer nicht mehr mitfährt, wird inaktiv
                gesetzt und bleibt an den alten Terminen stehen.
              */}
              <button
                type="button"
                className="mv-weg"
                title="Entfernen – nur ohne Einsätze möglich"
                onClick={() => loeschen.mutate(m.id)}
              >
                ×
              </button>
            </div>
          ))}
          {d.monteure.length === 0 && <span className="mv-leer">Noch niemand hinterlegt.</span>}
        </div>

        <div className="mv-neu">
          <input
            value={neu.name}
            placeholder="Name"
            onChange={(e) => setNeu((x) => ({ ...x, name: e.target.value }))}
          />
          <input
            value={neu.zusatz}
            placeholder={'Zusatz, etwa „Tischler"'}
            onChange={(e) => setNeu((x) => ({ ...x, zusatz: e.target.value }))}
          />
          <button
            type="button"
            className="mv-primaer"
            disabled={neu.name.trim().length < 2 || anlegen.isPending}
            onClick={() => anlegen.mutate()}
          >
            Hinzufügen
          </button>
        </div>
      </section>
    </div>
  );
}
